<template>
  <div class="client-list mb-5 mx-2">
    <h3 class="mb-3">Client List</h3>
    <div class="mb-3">
      <div class="card mb-3 table-card">
        <div class="card-header">
          <div class="row">
            <div class="col-md-6 col-12">
              <h3 class="mt-2">Clients ({{ local_num_clients }})</h3>
            </div>
            <div class="col-md-6 d-none d-md-block">
              <div class="d-flex justify-content-end gap-2">
                <div class="position-relative">
                  <input 
                    type="text" 
                    placeholder="Search by Name..." 
                    v-model="search_key" 
                    @input="debouncedSearchOptions"
                    class="search-box form-control me-1 py-2"
                   aria-label="Search by Name...">
                  <i class="fa fa-search search-placeholder-icon" v-show="search_key === ''"></i>
                </div>
                <button class="btn btn-secondary me-1 py-0 dropdown-toggle dropdown-toggle-no-btn" data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                  <img class="icon" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">Filters ({{ activeFilterCount }})
                </button>
                <div class="dropdown-menu dropdown-menu-primary p-3"  style="max-height: 450px; overflow-y: scroll;">
                  <div v-if="activeFilterCount > 0" class="mb-3">
                    <a href="#" @click.prevent="clearAllFilters" class="text-primary">
                      <i class="fa fa-times me-1"></i>Clear All Filters
                    </a>
                    <hr class="my-2">
                  </div>
                  <label class="form-label">Plan Level:</label>
                  <select class="form-select mb-2" v-model="filters.plan_level" @change="handleFilterSelection" aria-label="Plan Level:">
                    <option value="">All Plan Levels</option>
                    <option value="trial">Trial</option>
                    <option value="benchmarks">Benchmarks</option>
                    <option value="insights">Insights</option>
                  </select>

                  <label class="form-label">Agency:</label>
                  <select class="form-select mb-2" v-model="filters.agency" @change="handleFilterSelection" aria-label="Agency:">
                    <option value="">All Agencies</option>
                    <option v-for="agency in availableAgencies" :key="agency.id" :value="agency.id">{{ agency.name }}</option>
                  </select>

                  <label class="form-label">Integration:</label>
                  <select class="form-select mb-2" v-model="filters.integration" @change="handleFilterSelection" aria-label="Integration:">
                    <option value="">All Integrations</option>
                    <option v-for="integration in availableIntegrations" :key="integration.id" :value="integration.id">{{ integration.name }}</option>
                  </select>

                  <label class="form-label">Pathway:</label>
                  <select class="form-select mb-2" v-model="filters.pathway" @change="handleFilterSelection" aria-label="Pathway:">
                    <option value="">All Pathways</option>
                    <option v-for="pathway in availablePathways" :key="pathway.id" :value="pathway.id">{{ pathway.name }}</option>
                  </select>

                  <label class="form-label">Integration Error:</label>
                  <select class="form-select mb-2" v-model="filters.integration_error" @change="handleFilterSelection" aria-label="Integration Error:">
                    <option value="">All</option>
                    <option value="yes">Has Error</option>
                    <option value="no">No Error</option>
                  </select>

                  <label class="form-label">Pathway Error:</label>
                  <select class="form-select mb-2" v-model="filters.pathway_error" @change="handleFilterSelection" aria-label="Pathway Error:">
                    <option value="">All</option>
                    <option value="yes">Has Error</option>
                    <option value="no">No Error</option>
                  </select>
                </div>
                <a class="btn btn-primary float-end" href="/admin/clients/new">
                  <i class="fa fas fa-fw fa-plus"></i> Add Client
                </a>
              </div>
            </div>
          </div>
        </div>
        <div class="card-body p-0">
          <div class="table-width-responsive">
            <table class="table table-with-pagination mb-0">
              <thead>
                <tr class="table-header-row">
                  <th @click.prevent="sortByKey('id')" class="d-table-cell">
                    <div class="d-flex flex-row">
                      ID
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'id' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'id' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <th @click.prevent="sortByKey('name')" class="d-table-cell">
                    <div class="d-flex flex-row">
                      Name
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'name' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'name' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <th  class="d-none d-lg-table-cell">
                    <div class="d-flex flex-row">
                      Agency
                    </div>
                  </th>
                  <th @click.prevent="sortByKey('plan_level')" class="d-none d-md-table-cell">
                    <div class="d-flex flex-row">
                      Plan Level
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'plan_level' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'plan_level' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <th @click.prevent="sortByKey('integrations')" class="d-none d-xl-table-cell">
                    <div class="d-flex flex-row">
                      Integrations
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'integrations' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'integrations' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <th @click.prevent="sortByKey('pathways')" class="d-none d-xl-table-cell">
                    <div class="d-flex flex-row">
                      Pathways
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'pathways' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'pathways' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <!-- <th @click.prevent="sortByKey('integration_status')" class="d-table-cell">
                    <div class="d-flex flex-row">
                      Integration Status
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'integration_status' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'integration_status' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th> -->
                  <th @click.prevent="sortByKey('last_sync_date')" class="d-none d-md-table-cell">
                    <div class="d-flex flex-row">
                      Last Sync Date
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'last_sync_date' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'last_sync_date' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <th @click.prevent="sortByKey('last_gift_date')" class="d-none d-md-table-cell">
                    <div class="d-flex flex-row">
                      Last Gift Date
                      <div class="d-flex flex-column">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{ 'sort-key-active': sort_key === 'last_gift_date' && sort_order === 'asc' }" alt="">
                        <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{ 'sort-key-active': sort_key === 'last_gift_date' && sort_order === 'des' }" alt="">
                      </div>
                    </div>
                  </th>
                  <th class="column-sort-disabled text-center pe-3 d-table-cell" width="1">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="isLoadingList">
                  <td colspan="9" class="text-center py-4">
                    <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading clients...
                  </td>
                </tr>
                <tr v-for="client in list_shown" :key="client.id" v-show="!isLoadingList">
                  <td class="d-table-cell">{{ client.id }}</td>
                  <td class="d-table-cell align-start">
                    <div class="d-flex align-items-center text-wrap">
                      <img :src="client.image" :alt="client.name" class="avatar rounded-circle avatar-sm bg-white me-2">
                      <a :href="`/admin/${client.url}/edit`" class="text-decoration-none">{{ propercase(client.name) }}</a>
                    </div>
                  </td>
                  <td class="d-none d-lg-table-cell overlap-image-container agency-column" style="width:160px;" @mouseover="showAgencies(client)" @mouseleave="hideAgencies(client)">
                    <template v-for="(agency, idx) in client.agencies.slice(0,4)" :key="agency.id">
                      <a v-if="client.agencies && client.agencies.length > 0" :href="`/admin/agencies/${agency.id}/edit`" class="position-relative text-decoration-none">
                        <img v-if="agency.logo" :class="(client.agencies.length > 4) ? 'overlap-fading-img-'+idx : 'overlap-img-'+idx" :src="agency.logo" class="avatar rounded-circle rounded-circle-primary-border bg-white" :title="agency.name">
                      </a>
                    </template>
                    <div v-if="client.agencies && client.agencies.length > 0 && client.if_show_agencies" class="show-all-info-div px-2 pt-2">
                      <p v-for="agency in client.agencies" :key="agency.id">
                        <img v-if="agency.logo" :src="agency.logo" class="avatar rounded-circle rounded-circle-primary-border bg-white me-2">
                        {{ agency.name }}
                      </p>
                    </div>
                  </td>
                  <td class="d-none d-md-table-cell">{{ propercase(client.plan_level) }}</td>
                  <td class="d-none d-xl-table-cell integration-column overlap-image-container">
                    <a v-if="client.connections && client.connections.length && hasValidIntegrations(client)" :href="`/settings/${client.url}/integrations`" class="position-relative text-decoration-none">
                      <template v-for="(connection, idx) in getValidConnections(client)" :key="connection.id">
                        <div v-if="connection.integration && connection.integration.logo_url" :class="'overlap-img-'+idx + ' connection-image-container'" :title="getIntegrationTooltip(connection)" data-bs-toggle="tooltip" data-bs-html="true">
                          <img :src="connection.integration.logo_url" class="avatar rounded-circle rounded-circle-primary-border bg-white">
                          <div v-if="hasIntegrationError(connection)" class="error-indicator">
                            <i class="fas fa-times text-danger"></i>
                          </div>
                        </div>
                      </template>
                    </a>
                  </td>
                  <td class="d-none d-xl-table-cell pathway-column overlap-image-container">
                    <a v-if="getValidPathwayConnections(client).length > 0" :href="`/pathways/${client.url}/manage_pathways`" class="position-relative text-decoration-none">
                      <template v-for="(connection, idx) in getValidPathwayConnections(client)" :key="connection.id">
                        <div v-if="connection.integration && connection.integration.logo_url" :class="'overlap-img-'+idx + ' connection-image-container'" :title="getPathwayTooltip(connection)" data-bs-toggle="tooltip" data-bs-html="true">
                          <img :src="connection.integration.logo_url" class="avatar rounded-circle rounded-circle-primary-border bg-white">
                          <div v-if="hasPathwayError(connection)" class="error-indicator">
                            <i class="fas fa-times text-danger"></i>
                          </div>
                        </div>
                      </template>
                    </a>
                  </td>
                  <!-- <td class="d-table-cell text-center">
                    <i v-if="hasConnectionErrors(client)" 
                       class="fas fa-times text-danger"
                       data-bs-toggle="tooltip" 
                       data-bs-placement="top"
                       :title="getIntegrationStatusTooltip(client)"></i>
                    <i v-else class="fas fa-check green"></i>
                  </td> -->
                  <td class="d-none d-md-table-cell text-center">
                    <span :title="datetime(client.last_sync_date)" data-bs-toggle="tooltip">
                      {{ utc_date(client.last_sync_date) }}
                    </span>
                  </td>
                  <td class="d-none d-md-table-cell text-center">
                    <span :title="datetime(client.last_gift_date)" data-bs-toggle="tooltip">
                      {{ utc_date(client.last_gift_date) }}
                    </span>
                  </td>
                  <td class="table-column-action d-table-cell">
                    <div class="dropdown d-flex justify-content-center">
                      <button class="btn btn-white btn-icon-only-md" data-bs-toggle="dropdown" aria-expanded="false" aria-label="Button">
                        <img class="icon" src="/img/icons/avid-icons/dots-vertical.svg" data-bs-toggle="tooltip" title="Actions" alt="">
                      </button>
                      <div class="dropdown-menu dropdown-menu-primary dropdown-menu-end">
                        <a class="dropdown-item" :href="`/admin/${client.url}/edit`">
                          <img class="icon me-2" src="/img/icons/avid-icons/edit.svg" alt=""> Edit
                        </a>
                        <a class="dropdown-item" :href="`/admin/${client.url}/documents`">
                          <img class="icon me-2" src="/img/icons/avid-icons/file-text.svg" alt=""> Manage Files
                        </a>
                        <a v-if="client.is_onboarding" class="dropdown-item" :href="`/admin/${client.url}/tasks`">
                          <img class="icon me-2" src="/img/icons/avid-icons/file-text.svg" alt=""> Manage Onboarding Tasks
                        </a>
                        <a class="dropdown-item" :href="`/insights/${client.url}`">
                          <img class="icon me-2" src="/img/icons/avid-icons/eye.svg" alt=""> View Insights
                        </a>
                        <a class="dropdown-item" href="#" @click.prevent="refreshBlend(client)">
                          <img class="icon me-2" src="/img/icons/avid-icons/sync.svg" alt=""> Refresh Blend
                        </a>
                        <a class="dropdown-item" href="#" @click.prevent="clearCache(client)">
                          <img class="icon me-2" src="/img/icons/avid-icons/refresh.svg" alt=""> Clear Cache
                        </a>
                        <hr class="my-1">
                        <a class="dropdown-item text-danger" href="#" @click.prevent="deleteClient(client)">
                          <img class="icon red-icon me-2" src="/img/icons/avid-icons/trash.svg" alt=""> Delete Client
                        </a>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr v-if="!isLoadingList && list_shown.length === 0">
                  <td colspan="9" class="text-center py-3">No clients found. <a href="/admin/clients/new">Create a new client</a>.</td>
                </tr>
              </tbody>
              <tfoot class="table-border-bottom-0">
                <tr>
                  <td colspan="100%" class="py-0">
                    <div class="table-pagination">
                      <div class="table-pagination--group d-none d-md-flex">
                        <div class="position-relative">
                          <select class="form-select" v-model="num_per_page" @change="onPerPageChange" aria-label="Num Per Page">
                            <option v-for="num in num_per_page_options" :key="num" :value="num">{{ num }}</option>
                          </select>
                          <div class="select-display-text">
                            Showing {{ num_per_page }}
                          </div>
                        </div>
                      </div>
                      <div class="table-pagination--group d-none d-lg-flex">
                        {{ local_num_clients == 0 ? '0' : item_interval }} of {{ local_num_clients }}
                      </div>
                      <div class="table-pagination--group">
                        <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md me-1" @click.prevent="moveToPrevPage(filtered_clients)" :disabled="page_index === 0" data-bs-toggle="tooltip" title="Previous page">
                          <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="">
                        </button>
                        <div class="d-flex page-selector" v-if="page_index_array.length <= 3">
                          <button v-for="page in page_index_array" :key="page" class="btn btn-none btn-icon-only-md" :class="{ 'current-page': page === (page_index + 1) }" @click.prevent="moveToPage(page, filtered_clients)">
                            {{ page }}
                          </button>
                        </div>
                        <div class="d-flex page-selector" v-else>
                          <button v-for="page in page_index_array.slice(0,2)" :key="page" class="btn btn-none btn-icon-only-md" :class="{ 'current-page': page === (page_index + 1) }" @click.prevent="moveToPage(page, filtered_clients)">
                            {{ page }}
                          </button>
                          <button v-if="page_index > 2" class="btn btn-none btn-icon-only-md text-nowrap" disabled>
                          ...
                          </button>
                          <button v-if="page_index >= 2 && page_index < page_index_array.length -1" class="btn btn-none btn-icon-only-md current-page">
                            {{ page_index + 1 }}
                          </button>
                          <button v-if="page_index < page_index_array.length -2" class="btn btn-none btn-icon-only-md text-nowrap" disabled aria-hidden="true">
                          ...
                          </button>
                          <button :class="{ 'current-page': page_index === (page_index_array.length -1) }" class="btn btn-none btn-icon-only-md" @click.prevent="moveToPage(page_index_array.length, filtered_clients)" :key="page_index_array.length">
                            {{ page_index_array.length }}
                          </button>
                        </div>
                        <button v-if="page_count > 1" class="btn btn-secondary btn-icon-only-md ms-1" @click.prevent="moveToNextPage(filtered_clients)" :disabled="page_index === page_count -1" data-bs-toggle="tooltip" title="Next page">
                          <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="">
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import tablePaginationMixin from '../../mixins/table_pagination';
import Swal from 'sweetalert2';
import { useNotifications } from '../../composables/useNotifications';
import debounce from 'lodash.debounce';

export default {
  name: 'ClientList',
  props: {
    clientList: {
      type: Array,
      default: () => []
    },
    num_clients: {
      type: Number,
      default: 0
    }
  },
  setup() {
    const { showNotification } = useNotifications();
    return {
      showNotification
    };
  },
  mixins: [tablePaginationMixin],
  data() {
    return {
      filtered_clients: [],
      search_key: '',
      sort_key: 'name',
      sort_order: 'asc',
      resizeTimeout: null,
      local_num_clients: null,
      filters: {
        plan_level: '',
        agency: '',
        integration: '',
        pathway: '',
        integration_error: '',
        pathway_error: ''
      },
      availableAgencies: [],
      availableIntegrations: [],
      availablePathways: [],
      isLoadingList: false
    };
  },
  computed: {
    activeFilterCount() {
      let count = 0;
      if (this.filters.plan_level) count++;
      if (this.filters.agency) count++;
      if (this.filters.integration) count++;
      if (this.filters.pathway) count++;
      if (this.filters.integration_error) count++;
      if (this.filters.pathway_error) count++;
      return count;
    }
  },
  watch: {
    clientList: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.filtered_clients = [...newVal];
          this.reloadPagination();
        }
      },
      immediate: true
    },
    num_clients: {
      handler(newVal) {
        this.local_num_clients = newVal;
      },
      immediate: true
    }
  },
  beforeMount() {
    this.local_num_clients = this.num_clients;
    this.debouncedSearchOptions = debounce(this.searchClients, 500);
  },

  mounted() {
    // Initialize filter options
    this.loadPlanLevels();
    
    // Initialize with pre-processed data from server
    this.filtered_clients = [...this.clientList];
    this.reloadPagination(this.filtered_clients);
    this.initializeTooltips();
    
    // Wait for DOM to be fully rendered before calculating positions
    this.$nextTick(() => {
      // Add a small delay to ensure table layout is fully calculated
      setTimeout(() => {
        this.forceRecalculatePositions();
      }, 100);
    });
    
    // Add resize listener for responsive behavior
    window.addEventListener('resize', this.handleResize);
    
    // Add mutation observer to watch for DOM changes
    this.observer = new MutationObserver(() => {
      this.handleTableVisibilityChange();
    });
    
    // Start observing the table container
    const tableContainer = document.querySelector('.table-card');
    if (tableContainer) {
      this.observer.observe(tableContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }
  },
  beforeDestroy() {
    // Clean up resize listener
    window.removeEventListener('resize', this.handleResize);
    
    // Clean up mutation observer
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  updated() {
    // Recalculate positions when component is updated
    this.$nextTick(() => {
      this.forceRecalculatePositions();
    });
  },
  methods: {
    extractUniqueItems(arrayKey, idPath, namePath, objectKey = null, additionalCondition = null) {
      const items = new Map();
      
      this.clientList.forEach(client => {
        const array = client[arrayKey];
        if (!array) return;
        
        array.forEach(item => {
          // Handle nested object paths (e.g., 'integration.id')
          const id = this.getNestedValue(item, idPath);
          const name = this.getNestedValue(item, namePath);
          
          if (!id || !name) return;
          
          // Check additional condition if provided
          if (additionalCondition) {
            const conditionValue = this.getNestedValue(item, additionalCondition);
            if (!conditionValue) return;
          }
          
          items.set(id, name);
        });
      });
      
      return Array.from(items.entries()).map(([id, name]) => ({ id, name }));
    },
    
    getNestedValue(obj, path) {
      return path.split('.').reduce((current, key) => current && current[key], obj);
    },
    
    async loadPlanLevels() {
      try {
        const response = await window.axios.get('/api/admin/clients/filter_options');
        this.availablePlanLevels = response.data.plan_levels || [];
        this.availableAgencies = response.data.agencies || [];
        this.availableIntegrations = response.data.integrations || [];
        this.availablePathways = response.data.pathways || [];

      } catch (error) {
        console.error('Error loading plan levels:', error);
        // Fallback to extracting from clients data if API fails
        this.availablePlanLevels = this.extractPlanLevelsFromClients();
      }
    },
    
    extractPlanLevelsFromClients() {
      const planLevels = new Set();
      
      this.clientList.forEach(client => {
        if (client.plan_level && client.plan_level.trim() !== '') {
          planLevels.add(client.plan_level);
        }
      });
      
      return Array.from(planLevels).sort().map(planLevel => ({
        id: planLevel,
        name: planLevel.charAt(0).toUpperCase() + planLevel.slice(1)
      }));
    },
    
    getClientCensusConnections(client) {
      if (!client.connections) return [];
      return client.connections.filter(conn => 
        conn.census_connections && conn.census_connections.length > 0
      );
    },
    
    handleFilterSelection() {
      // Reset to first page when filters change
      this.page_index = 0;

      // Check if we have any active filters or search term
      const hasFilters = this.filters.plan_level || this.filters.agency || 
                        this.filters.integration || this.filters.pathway || 
                        this.filters.integration_error || this.filters.pathway_error;
      
      const hasSearchTerm = this.search_key.trim() !== '';

      // If no filters and no search term, use original client list
      if (!hasFilters && !hasSearchTerm) {
        this.filtered_clients = [...this.clientList];
        this.local_num_clients = this.num_clients;
        this.reloadPagination();
        return;
      }

      // For small datasets without search term, filter locally
      if (!hasSearchTerm && this.num_clients < 100) {
        const term = this.search_key.trim().toLowerCase();
        this.filtered_clients = this.clientList.filter(client => {
          // Plan level filter
          if (this.filters.plan_level && client.plan_level !== this.filters.plan_level) {
            return false;
          }
          
          // Agency filter
          if (this.filters.agency && (!client.agencies || !client.agencies.some(agency => agency.id === this.filters.agency))) {
            return false;
          }
          
          // Integration filter
          if (this.filters.integration && (!client.connections || !client.connections.some(connection => 
            connection.integration && connection.integration.id === this.filters.integration))) {
            return false;
          }
          
          // Pathway filter
          if (this.filters.pathway && (!client.connections || !client.connections.some(connection => 
            connection.integration && connection.integration.id === this.filters.pathway && connection.census_destination_id))) {
            return false;
          }
          
          // Integration error filter
          if (this.filters.integration_error) {
            const hasIntegrationError = this.hasConnectionErrors(client);
            if (this.filters.integration_error === 'yes' && !hasIntegrationError) {
              return false;
            }
            if (this.filters.integration_error === 'no' && hasIntegrationError) {
              return false;
            }
          }
          
          // Pathway error filter
          if (this.filters.pathway_error) {
            const hasPathwayError = client.connections && client.connections.some(connection => 
              this.hasPathwayError(connection)
            );
            if (this.filters.pathway_error === 'yes' && !hasPathwayError) {
              return false;
            }
            if (this.filters.pathway_error === 'no' && hasPathwayError) {
              return false;
            }
          }
          
          return true;
        });
        this.local_num_clients = this.filtered_clients.length;
        this.reloadPagination();
      } else {
        // Use server-side filtering for large datasets or when search is involved
        this.getFromDB();
      }

      this.$nextTick(() => {
        this.initializeTooltips();
        this.forceRecalculatePositions();
      });
    },
    sortData() {
      const key = this.sort_key;
      this.filtered_clients.sort((a, b) => {
        const valA = a[key] ?? '';
        const valB = b[key] ?? '';
        
        // Numeric ID field
        if (key === 'id') {
          const numA = Number(valA) || 0;
          const numB = Number(valB) || 0;
          return this.sort_order === 'asc' ? numA - numB : numB - numA;
        }
        
        // Date fields
        if (key === 'last_sync_date' || key === 'last_gift_date') {
          const dateA = valA ? new Date(valA) : null;
          const dateB = valB ? new Date(valB) : null;
          if (!dateA && !dateB) return 0;
          if (!dateA) return this.sort_order === 'asc' ? -1 : 1;
          if (!dateB) return this.sort_order === 'asc' ? 1 : -1;
          return this.sort_order === 'asc' ? dateA - dateB : dateB - dateA;
        }
        
        // Array-based sorting for agencies and integrations
        if (key === 'agency' || key === 'agencies') {
          const agenciesA = a.agencies || [];
          const agenciesB = b.agencies || [];
          const nameA = agenciesA.length > 0 ? agenciesA[0].name || '' : '';
          const nameB = agenciesB.length > 0 ? agenciesB[0].name || '' : '';
          return this.sort_order === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        }
        
        if (key === 'integrations') {
          const connectionsA = this.getValidConnections(a);
          const connectionsB = this.getValidConnections(b);
          const nameA = connectionsA.length > 0 ? connectionsA[0].integration?.name || '' : '';
          const nameB = connectionsB.length > 0 ? connectionsB[0].integration?.name || '' : '';
          return this.sort_order === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        }
        
        if (key === 'pathways') {
          const pathwayConnectionsA = this.getValidPathwayConnections(a);
          const pathwayConnectionsB = this.getValidPathwayConnections(b);
          const nameA = pathwayConnectionsA.length > 0 ? pathwayConnectionsA[0].integration?.name || '' : '';
          const nameB = pathwayConnectionsB.length > 0 ? pathwayConnectionsB[0].integration?.name || '' : '';
          return this.sort_order === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        }
        
        if (key === 'integration_status') {
          const hasErrorsA = this.hasConnectionErrors(a);
          const hasErrorsB = this.hasConnectionErrors(b);
          // Sort by error status: clients with errors first in ascending order
          if (hasErrorsA !== hasErrorsB) {
            return this.sort_order === 'asc' ? (hasErrorsA ? -1 : 1) : (hasErrorsA ? 1 : -1);
          }
          // If both have same error status, sort by client name
          return this.sort_order === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
        }
        
        // String comparison
        if (typeof valA === 'string' && typeof valB === 'string') {
          return this.sort_order === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
        }
        
        // Generic comparison
        if (valA < valB) return this.sort_order === 'asc' ? -1 : 1;
        if (valA > valB) return this.sort_order === 'asc' ? 1 : -1;
        return 0;
      });
      this.reloadPagination(this.filtered_clients);
      this.$nextTick(() => {
        this.initializeTooltips();
        this.forceRecalculatePositions();
      });
    },
    sortByKey(key) {
      if (this.sort_key === key) {
        this.sort_order = this.sort_order === 'asc' ? 'des' : 'asc';
      } else {
        this.sort_key = key;
        this.sort_order = 'asc';
      }
      
      // Reset to first page when sorting changes
      this.page_index = 0;
      
      // Check if we should use server-side or client-side sorting
      if (this.hasActiveFilters() || this.search_key || this.local_num_clients >= 100) {
        // Use server-side sorting for filtered/searched/large datasets
        this.getFromDB();
      } else {
        // Use client-side sorting for small unfiltered datasets
        this.sortData();
      }
    },
    deleteClient(client) {
      Swal.fire({
        title: "Are you sure you want to delete this client?",
        text: "This client will be removed and all data will be deleted. This action cannot be undone!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete",
        customClass: {
          popup: 'swal-warning'
        },
      }).then((result) => {
        if (result.isConfirmed) {
          window.axios.delete(`/api/client/${client.url}`).then(response => {
            // Remove from both arrays
            this.clientList.splice(this.clientList.findIndex(c => c.id === client.id), 1);
            this.local_num_clients--;
            this.filtered_clients.splice(this.filtered_clients.findIndex(c => c.id === client.id), 1);
            this.reloadPagination(this.filtered_clients);
            this.$nextTick(() => {
              this.forceRecalculatePositions();
            });
            
            this.showNotification({
              text: '<strong>' + client.name + '</strong> has been deleted!'
            });
          }).catch(error => {
            Swal.fire({ 
              title: 'Error', 
              text: 'Unable to delete client.', 
              icon: 'error', 
              confirmButtonText: 'OK', 
              customClass: { popup: 'swal-error' } 
            });
          });
        }
      });
    },
    async refreshBlend(client) {
      try {
        await window.axios.post(`/api/data_blend/${client.url}/refresh`);
        await Swal.fire({
          title: "The Data is Rebuilding",
          text: "The data blend will be rebuilt and all audiences will be resynced within a few minutes.",
          icon: "success",
          customClass: {
            popup: 'swal-success'
          },
        });
      } catch (error) {
        await Swal.fire({
          title: "Error",
          text: "Unable to refresh data blend.",
          icon: "error",
          confirmButtonText: "OK",
          customClass: { popup: 'swal-error' }
        });
      }
    },
    async clearCache(client) {
      try {
        await window.axios.get(`/api/client/${client.url}/clear_cache`);
        this.showNotification({
          text: 'Cache cleared for <strong>' + client.name + '</strong>!'
        });
      } catch (error) {
        await Swal.fire({
          title: "Error",
          text: "Unable to clear cache.",
          icon: "error",
          confirmButtonText: "OK",
          customClass: { popup: 'swal-error' }
        });
      }
    },
    showAgencies(client) {
      client.if_show_agencies = true;
    },
    hideAgencies(client) {
      client.if_show_agencies = false;
    },
    hasValidIntegrations(client) {
      return client.connections && client.connections.some(connection => connection && connection.integration);
    },
    getValidConnections(client) {
      // Skip File Upload integration
      return client.connections ? client.connections.filter(connection => 
        connection && 
        connection.integration && 
        connection.integration.id !== '71b0c3b0-8390-49ec-9984-1c04b7384d38'
      ) : [];
    },
    getValidPathwayConnections(client) {
      return client.connections ? client.connections.filter(connection => 
        connection && 
        connection.integration && 
        connection.census_destination_id && 
        connection.census_destination_objects && 
        connection.census_destination_objects.length > 0
      ) : [];
    },

    hasConnectionError(connection) {
      if (!connection) return false;
      
      // Skip File Upload integration
      if (connection.integration && connection.integration.id === '71b0c3b0-8390-49ec-9984-1c04b7384d38') {
        return false;
      }
      
      // Check for direct sync_error_msg (but not the string "null")
      const hasDirectError = connection && connection.sync_error_msg && connection.sync_error_msg !== "null";
      
      // Check for census sync errors
      const hasCensusError = connection && connection.census_connections && connection.census_connections.some(censusConn => 
        censusConn.syncs && censusConn.syncs.some(sync => 
          sync.last_sync_status === 'failed' && sync.last_sync_error_message
        )
      );
      
      return hasDirectError || hasCensusError;
    },
    
    hasIntegrationError(connection) {
      if (!connection) return false;
      
      // Skip File Upload integration
      if (connection.integration && connection.integration.id === '71b0c3b0-8390-49ec-9984-1c04b7384d38') {
        return false;
      }
      
      // Only show integration errors for direct sync errors (not pathway/census errors)
      return connection && connection.sync_error_msg && connection.sync_error_msg !== "null";
    },
    
    hasPathwayError(connection) {
      if (!connection) return false;
      
      // Skip File Upload integration
      if (connection.integration && connection.integration.id === '71b0c3b0-8390-49ec-9984-1c04b7384d38') {
        return false;
      }
      
      // Only show pathway errors for census sync errors
      return connection && connection.census_connections && connection.census_connections.some(censusConn => 
        censusConn.syncs && censusConn.syncs.some(sync => 
          sync.last_sync_status === 'failed' && sync.last_sync_error_message
        )
      );
    },
    
    hasConnectionErrors(client) {
      if (!client.connections || client.connections.length === 0) return false;
      
      return client.connections.some(conn => this.hasConnectionError(conn));
    },
    getConnectionsWithErrors(client) {
      if (!client.connections) return [];
      return client.connections.filter(conn => this.hasConnectionError(conn));
    },
    getLastSync(conn_id, client) {
      let lastSync = null;
      let lastFailedSync = null;
      let mostRecentDate = null;

      // Get census connections for this specific client
      const census_connections = this.getClientCensusConnections(client);

      // Get all syncs for this connection
      for (let i = 0; i < census_connections.length; i++) {
        if (census_connections[i].id == conn_id && census_connections[i].census_connections) {
          for (let j = 0; j < census_connections[i].census_connections.length; j++) {
            const census_conn = census_connections[i].census_connections[j];
            if (census_conn.syncs && census_conn.syncs.length > 0) {
              // Check each sync
              for (let k = 0; k < census_conn.syncs.length; k++) {
                const sync = census_conn.syncs[k];
                
                // If this sync failed, keep track of it
                if (sync.last_sync_status === 'failed') {
                  if (!lastFailedSync || new Date(sync.last_synced_at) > new Date(lastFailedSync.last_synced_at)) {
                    lastFailedSync = sync;
                  }
                }
                
                // Keep track of most recent sync
                if (!mostRecentDate || new Date(sync.last_synced_at) > mostRecentDate) {
                  mostRecentDate = new Date(sync.last_synced_at);
                  lastSync = sync;
                }
              }
            }
          }
        }
      }

      // Return failed sync if one exists, otherwise return most recent
      return lastFailedSync || lastSync;
    },
    getLastSyncStatus(conn_id, client) {
      const last_sync = this.getLastSync(conn_id, client);
      if (last_sync != null) {
        return last_sync.last_sync_status;
      }
      return null;
    },
    getLastSyncErrorMessage(conn_id, client) {
      const last_sync = this.getLastSync(conn_id, client);
      if (last_sync != null) {
        return '<strong>Pathway Error:</strong> ' + last_sync.last_sync_error_message;
      }
      return null;
    },
    getIntegrationTooltip(connection) {
      if (!connection || !connection.integration) {
        return 'Unknown Integration';
      }
      
      let tooltipText = connection.integration.name || connection.integration.display_name || 'Unknown Integration';
      
      // Only show integration error (direct sync error) in integration column
      if (connection.sync_error_msg && connection.sync_error_msg !== "null") {
        tooltipText += `<br><br><strong>Integration Error:</strong> ${connection.sync_error_msg}`;
        if (connection.error_reference_id) {
          tooltipText += ` (Reference: ${connection.error_reference_id})`;
        }
      }
      
      return tooltipText;
    },
    
    getPathwayTooltip(connection) {
      if (!connection || !connection.integration) {
        return 'Unknown Integration';
      }
      
      let tooltipText = connection.integration.display_name + ' (' + connection.census_destination_objects.length + ')';
      
      // Only show pathway error (census sync error) in pathway column
      if (connection.census_connections) {
        for (const censusConn of connection.census_connections) {
          if (censusConn.syncs) {
            for (const sync of censusConn.syncs) {
              if (sync.last_sync_status === 'failed' && sync.last_sync_error_message) {
                tooltipText += `<br><br><strong>Pathway Error:</strong> ${sync.last_sync_error_message}`;
                break;
              }
            }
          }
          if (tooltipText.includes('Pathway Error:')) break;
        }
      }
      
      return tooltipText;
    },
    
    getConnectionTooltip(connection) {
      if (!connection || !connection.integration) {
        return 'Unknown Integration';
      }
      
      let tooltipText = connection.integration.name || connection.integration.display_name || 'Unknown Integration';
      
      // Check for integration error (direct sync error)
      if (connection.sync_error_msg && connection.sync_error_msg !== "null") {
        tooltipText += `<br><br><strong>Integration Error:</strong> ${connection.sync_error_msg}`;
        if (connection.error_reference_id) {
          tooltipText += ` (Reference: ${connection.error_reference_id})`;
        }
      }
      
      // Check for pathway error (census sync error)
      if (connection.census_connections) {
        for (const censusConn of connection.census_connections) {
          if (censusConn.syncs) {
            for (const sync of censusConn.syncs) {
              if (sync.last_sync_status === 'failed' && sync.last_sync_error_message) {
                tooltipText += `<br><br><strong>Pathway Error:</strong> ${sync.last_sync_error_message}`;
                break;
              }
            }
          }
          if (tooltipText.includes('Pathway Error:')) break;
        }
      }
      
      return tooltipText;
    },
    
    getIntegrationStatusTooltip(client) {
      if (this.hasConnectionErrors(client)) {
        const errorConnections = this.getConnectionsWithErrors(client);
        let tooltipText = `${errorConnections.length} connection(s) have errors:\n\n`;
        errorConnections.forEach(conn => {
          let errorMsg = '';
          
          // Check for direct sync error
          if (conn.sync_error_msg && conn.sync_error_msg !== "null") {
            errorMsg = conn.sync_error_msg;
          }
          // Check for census sync error
          else if (conn.census_connections) {
            for (const censusConn of conn.census_connections) {
              if (censusConn.syncs) {
                for (const sync of censusConn.syncs) {
                  if (sync.last_sync_status === 'failed' && sync.last_sync_error_message) {
                    errorMsg = sync.last_sync_error_message;
                    break;
                  }
                }
              }
              if (errorMsg) break;
            }
          }
          
          tooltipText += `• ${conn.integration.display_name}: ${errorMsg}\n`;
        });
        return tooltipText;
      }
      return 'All connections are working properly';
    },
    initializeTooltips() {
      // Destroy existing tooltips first
      const tooltipElements = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      tooltipElements.forEach(element => {
        const tooltip = bootstrap.Tooltip.getInstance(element);
        if (tooltip) {
          tooltip.dispose();
        }
      });
      
      // Initialize tooltips for all elements with data-bs-toggle="tooltip"
      tooltipElements.forEach(element => {
        new bootstrap.Tooltip(element, {
          placement: 'top',
          trigger: 'hover',
          html: true,
          delay: { show: 0, hide: 100 }
        });
      });
    },
    calculateOverlapPositions() {
      // Calculate dynamic overlap positions for all overlap containers
      const overlapContainers = document.querySelectorAll('.overlap-image-container');
      
      overlapContainers.forEach(container => {
        // Ensure container is visible and has dimensions
        if (!container.offsetWidth || !container.offsetHeight) {
          // Try again after a short delay if dimensions are not available
          setTimeout(() => {
            if (container.offsetWidth && container.offsetHeight) {
              this.calculateOverlapPositions();
            }
          }, 50);
          return; // Skip if container dimensions are not available
        }
        
        const overlapImages = container.querySelectorAll('[class*="overlap-img-"]');
        const totalImages = overlapImages.length;
        
        if (totalImages > 0) {
          // Get screen size and determine max items per row
          const screenWidth = window.innerWidth;
          let maxItemsPerRow = 4; // Default
          
          // Check if this is an integration or pathway column
          const isIntegrationColumn = container.classList.contains('integration-column');
          const isPathwayColumn = container.classList.contains('pathway-column');
          
          if (screenWidth >= 1400) { // xxl
            maxItemsPerRow = 8;
          } else if (screenWidth >= 1200) { // xl
            maxItemsPerRow = 6;
          } else { // lg and smaller
            maxItemsPerRow = 0; // Hide column
          }
          
          if (maxItemsPerRow === 0) {
            // Hide all images on smaller screens
            overlapImages.forEach(image => {
              image.style.display = 'none';
            });
            return;
          }
          
          // Calculate spacing based on container width and max items
          const containerWidth = container.offsetWidth || 160; // Fallback width if not available
          const imageWidth = 32; // Standard image width
          const maxSpacing = 27; // Maximum spacing between images
          const minSpacing = 20; // Minimum spacing between images
          
          // Calculate optimal spacing for the first row
          let spacing = maxSpacing;
          if (maxItemsPerRow > 1) {
            const availableSpace = containerWidth - imageWidth;
            spacing = Math.min(maxSpacing, Math.max(minSpacing, availableSpace / (maxItemsPerRow - 1)));
          }
          
          // Track the maximum width and height needed for the parent <a> element
          let maxWidth = 0;
          let maxHeight = 0;
          
          // Apply positions to each image
          overlapImages.forEach((image, index) => {
            if (index < maxItemsPerRow) {
              // First row items
              const leftPosition = index * spacing;
              image.style.setProperty('--overlap-left', `${leftPosition}px`);
              image.style.top = '0px';
              image.style.display = 'block';
              
              // Calculate width needed for this image
              const imageRightEdge = leftPosition + imageWidth;
              maxWidth = Math.max(maxWidth, imageRightEdge);
              maxHeight = Math.max(maxHeight, 32); // 32px height
            } else if (index < maxItemsPerRow * 2) {
              // Second row items (wrapped)
              const secondRowIndex = index - maxItemsPerRow;
              const leftPosition = secondRowIndex * spacing;
              image.style.setProperty('--overlap-left', `${leftPosition}px`);
              image.style.top = '34px';
              image.style.display = 'block';
              // Add wrapped class for styling
              image.classList.add('overlap-img-wrapped');
              
              // Calculate width needed for this image
              const imageRightEdge = leftPosition + imageWidth;
              maxWidth = Math.max(maxWidth, imageRightEdge);
              maxHeight = Math.max(maxHeight, 66); // 34px top + 32px height = 66px total
            } else {
              // Hide additional images
              image.style.display = 'none';
            }
          });
          
          // Set the dimensions of the parent <a> element
          const parentLink = container.querySelector('a');
          if (parentLink) {
            if (maxWidth > 0) {
              parentLink.style.width = `${maxWidth}px`;
              parentLink.style.height = `${maxHeight}px`;
              parentLink.style.display = 'block';
            } else {
              // If no images are visible, set minimum dimensions
              parentLink.style.width = '32px';
              parentLink.style.height = '32px';
              parentLink.style.display = 'block';
            }
          }
        }
      });
    },
    handleResize() {
      // Debounce resize events for better performance
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.calculateOverlapPositions();
      }, 150); // Increased delay for resize events
    },
    
    // Method to force recalculation of overlap positions
    forceRecalculatePositions() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.calculateOverlapPositions();
        }, 50);
      });
    },
    
    // Method to handle table visibility changes
    handleTableVisibilityChange() {
      // Check if table is visible and has proper dimensions
      const table = document.querySelector('.table-with-pagination');
      if (table && table.offsetWidth > 0) {
        this.forceRecalculatePositions();
      }
    },

    clearAllFilters() {
      // Reset all filter values
      this.filters = {
        plan_level: '',
        agency: '',
        integration: '',
        pathway: '',
        integration_error: '',
        pathway_error: ''
      };
      
      // Trigger filter selection to refresh the data
      this.handleFilterSelection();
    },

    hasActiveFilters() {
      return this.filters.plan_level || 
             this.filters.agency || 
             this.filters.integration || 
             this.filters.pathway || 
             this.filters.integration_error || 
             this.filters.pathway_error;
    },

    /* ------------------------------
     * Server-side search & pagination helpers
     * ------------------------------ */
    onPerPageChange() {
      this.page_index = 0;
      // Always fetch from server when per-page changes so we get the correct number of rows
      // (e.g. switching 20 → 25 would otherwise still show 20 if we only had 20 loaded)
      this.getFromDB();
    },
    reloadPagination(list = this.filtered_clients) {
      // Show first page of currently filtered clients
      const limit = Math.min(Number(this.num_per_page), list.length);
      this.list_shown = list.slice(0, limit);
      this.page_count = Math.ceil(this.local_num_clients / this.num_per_page);
      this.page_index_array = Array.from({ length: this.page_count }, (_, i) => i + 1);
      this.getItemInterval();
    },
    getItemInterval() {
      if (this.local_num_clients > 0) {
        const from = this.page_index * this.num_per_page + 1;
        let to = (this.page_index + 1) * this.num_per_page;
        if (to > this.local_num_clients) to = this.local_num_clients;
        this.item_interval = from >= to ? `${to}` : `${from} - ${to}`;
      } else {
        this.item_interval = '0';
      }
    },
    getFromDB() {
      this.isLoadingList = true;
      const params = {
        pattern: '%' + this.search_key + '%',
        page_size: this.num_per_page,
        offset: this.num_per_page * this.page_index,
        sort_key: this.sort_key,
        sort_order: this.sort_order
      };

      // Add filter parameters
      if (this.filters.plan_level) params.plan_level = this.filters.plan_level;
      if (this.filters.agency) params.agency = this.filters.agency;
      if (this.filters.integration) params.integration = this.filters.integration;
      if (this.filters.pathway) params.pathway = this.filters.pathway;
      if (this.filters.integration_error) params.integration_error = this.filters.integration_error;
      if (this.filters.pathway_error) params.pathway_error = this.filters.pathway_error;

      window.axios.post('/api/admin/clients/list_by_pattern', params)
        .then(response => {
          this.filtered_clients = response.data.clients;
          this.local_num_clients = response.data.num_clients;
          this.reloadPagination();
        })
        .finally(() => {
          this.isLoadingList = false;
        });
    },
    moveToPrevPage(_list = null) {
      if (this.page_index > 0) {
        this.page_index--;
        this.getFromDB();
      }
    },
    moveToPage(page, _list = null) {
      this.page_index = page - 1;
      this.getFromDB();
    },
    moveToNextPage(_list = null) {
      if (this.page_index < this.page_count - 1) {
        this.page_index++;
        this.getFromDB();
      }
    },
    searchClients() {
      // Reset to first page
      this.page_index = 0;

      // Check if we have any active filters
      const hasFilters = this.filters.plan_level || this.filters.agency || 
                        this.filters.integration || this.filters.pathway || 
                        this.filters.integration_error || this.filters.pathway_error;

      if (this.search_key === '' && !hasFilters) {
        this.filtered_clients = [...this.clientList];
        this.local_num_clients = this.num_clients;
        this.reloadPagination();
      }  else { // Large data set or has filters – fetch from server
        this.getFromDB();
      }
    },

  }
};
</script>
